import React, { useState, useEffect } from 'react';
import { Search, Globe, Mail, Phone, MapPin, Loader2, ExternalLink, Briefcase } from 'lucide-react';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnerServices = () => {
  const [services, setServices] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');

  const partnerToken = localStorage.getItem('partnerToken');
  const axiosConfig = { headers: { Authorization: `Bearer ${partnerToken}` } };

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/partner/services`, axiosConfig);
      setServices(response.data.services || []);
      setServiceTypes(response.data.service_types || []);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const filteredServices = services.filter(s => {
    const matchesSearch = !searchTerm ||
      s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.service_offered?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.location?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'all' || s.service_offered === selectedType;
    return matchesSearch && matchesType;
  });

  const resolvePhoto = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    return `${BACKEND_URL}${photo}`;
  };

  const formatWebsite = (url) => {
    if (!url) return null;
    try {
      const u = new URL(url.startsWith('http') ? url : `https://${url}`);
      return u.hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  const getWebsiteHref = (url) => {
    if (!url) return '#';
    return url.startsWith('http') ? url : `https://${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4" data-testid="partner-services-loading">
        <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
        <p className="text-slate-400">Loading Services...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="partner-services">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Services</h1>
        <p className="text-slate-400 mt-1">Find trusted professionals and service providers</p>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Search by name, service, or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20"
            data-testid="search-services-input"
          />
        </div>
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="bg-slate-800 border border-slate-700 text-white rounded-md px-4 py-2 text-sm focus:border-amber-500 focus:ring-amber-500/20 focus:outline-none min-w-[180px]"
          data-testid="filter-service-type"
        >
          <option value="all">All Services</option>
          {serviceTypes.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      {/* Results Count */}
      <div className="text-sm text-slate-500">
        {filteredServices.length} service{filteredServices.length !== 1 ? 's' : ''} found
        {selectedType !== 'all' && <span className="text-amber-400 ml-1">in {selectedType}</span>}
      </div>

      {/* Service Cards Grid */}
      {filteredServices.length === 0 ? (
        <div className="text-center py-16">
          <div className="p-6 rounded-full bg-slate-800/50 inline-flex mb-4">
            <Briefcase className="w-12 h-12 text-slate-600" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">No Services Found</h2>
          <p className="text-slate-400">
            {searchTerm || selectedType !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'No services have been listed yet'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredServices.map((service) => (
            <div
              key={service.id}
              className="group flex bg-slate-900/80 border border-slate-800 rounded-xl overflow-hidden hover:border-slate-600 transition-all duration-300"
              data-testid={`service-card-${service.id}`}
            >
              {/* Left color strip based on service type */}
              <div className="w-1 flex-shrink-0 bg-gradient-to-b from-slate-500 to-slate-600 group-hover:from-slate-400 group-hover:to-slate-500 transition-colors" />

              {/* Photo */}
              <div className="flex-shrink-0 p-4 flex items-start">
                <div className="w-14 h-14 rounded-full overflow-hidden bg-slate-800 border border-slate-700">
                  {service.photo ? (
                    <img
                      src={resolvePhoto(service.photo)}
                      alt={service.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div
                    className={`w-full h-full items-center justify-center bg-slate-800 ${service.photo ? 'hidden' : 'flex'}`}
                  >
                    <span className="text-lg font-bold text-slate-400">
                      {service.name?.[0]?.toUpperCase() || '?'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 py-4 pr-4 min-w-0">
                {/* Name + Badge */}
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-semibold text-white leading-tight truncate">
                    {service.name}
                  </h3>
                  <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-slate-700/80 text-slate-300 border border-slate-600 whitespace-nowrap">
                    {service.service_offered}
                  </span>
                </div>

                {/* Contact row */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                  {service.contact_email && (
                    <a href={`mailto:${service.contact_email}`} className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors">
                      <Mail className="w-3.5 h-3.5 flex-shrink-0 text-slate-500" />
                      <span className="text-xs truncate max-w-[180px]">{service.contact_email}</span>
                    </a>
                  )}
                  {service.contact_mobile && (
                    <a href={`tel:${service.contact_mobile}`} className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors">
                      <Phone className="w-3.5 h-3.5 flex-shrink-0 text-slate-500" />
                      <span className="text-xs">{service.contact_mobile}</span>
                    </a>
                  )}
                  {service.location && (
                    <div className="flex items-center gap-1.5 text-slate-500">
                      <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="text-xs truncate max-w-[140px]">{service.location}</span>
                    </div>
                  )}
                </div>

                {/* Description if available */}
                {service.description && (
                  <p className="text-xs text-slate-500 mt-1.5 line-clamp-1">{service.description}</p>
                )}

                {/* Website */}
                {service.website && (
                  <a
                    href={getWebsiteHref(service.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 text-xs text-slate-400 hover:text-white transition-colors"
                    data-testid={`visit-website-${service.id}`}
                  >
                    <Globe className="w-3 h-3" />
                    <span>{formatWebsite(service.website)}</span>
                    <ExternalLink className="w-2.5 h-2.5 opacity-50" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PartnerServices;
